import { Button } from '@ledgerloom/ui';
import { useState } from 'react';
import { api } from '../api';
import { useApi } from '../hooks';
import { AskAgent, type ChatState } from './AskAgent';
import { ErrorBanner, Icon, Time } from './ui';

export const HELP_STARTERS = [
  'What does On trial mean?',
  'How do I put a form on my website?',
  'Why can\'t I see Give it a job on the browser agent?',
  'How do I hire an agent?',
];

interface Past { id: string; title: string; open: boolean; started_at: string; last_at: string }
// Which conversation shows: the latest, a new one not asked yet, one opened from the list, or the list.
type View = { kind: 'latest' } | { kind: 'new' } | { kind: 'thread'; id: string } | { kind: 'history' };

// The help agent's chat (docs/plans/help-agent-plan.md), in the Help button's
// panel and on the Help screen. Two buttons manage it: New conversation, and
// Past conversations (the last 30 days, each one reopened or emailed to you).
// The agent marks a question that starts a new topic, and the chat offers a
// new conversation; it never starts one by itself. Patterns:
// ai-intelligence/ai-chat, navigation/list for the past conversations.
export function HelpChat({ page, level, onClose, onState }: { page?: string; level: 2 | 3; onClose?: () => void; onState?: (state: ChatState) => void }) {
  const [view, setView] = useState<View>({ kind: 'latest' });
  const fresh = () => setView({ kind: 'new' });
  const Heading = level === 2 ? 'h2' : 'h3';
  const history = view.kind === 'history';
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row help-head">
        <Button type="button" variant="ghost" size="sm" aria-pressed={history} onClick={() => setView(history ? { kind: 'latest' } : { kind: 'history' })} title="Past conversations">
          <Icon name="history" /><span className="sr-only">Past conversations</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={fresh} title="New conversation"><Icon name="plus" /><span className="sr-only">New conversation</span></Button>
        {onClose && <Button type="button" variant="ghost" size="sm" onClick={onClose} title="Close help"><Icon name="x" /><span className="sr-only">Close help</span></Button>}
      </div>
      {history ? <section className="stack" style={{ gap: 8 }}><Heading>Past conversations</Heading><PastConversations open={(id) => setView({ kind: 'thread', id })} /></section> : (
        <AskAgent key={view.kind === 'thread' ? view.id : view.kind} base="/v1/help" title={view.kind === 'new' ? 'New conversation' : 'Ask the help agent'} level={level} fallbackName="help agent" starters={HELP_STARTERS}
          query={view.kind === 'thread' ? `?thread=${view.id}` : ''} fresh={view.kind === 'new'}
          body={{ ...(page ? { page } : {}), ...(view.kind === 'new' ? { new: true } : view.kind === 'thread' ? { thread_id: view.id } : {}) }}
          onAsked={(id) => setView({ kind: 'thread', id })} onNewTopic={fresh} onState={onState} onEnded={view.kind === 'thread' ? fresh : undefined}
          after={(t) => <EmailMe thread={t.id} />}
          hint={`{name} answers from the Mazu guide and your workspace${page ? ', starting from this screen' : ''}. It changes nothing.`} working="is looking it up" />
      )}
    </div>
  );
}

function PastConversations({ open }: { open: (id: string) => void }) {
  const list = useApi<Past[]>('/v1/help/conversations');
  if (list.error) return <ErrorBanner error={list.error} />;
  if (!list.data) return <div className="faint" aria-busy="true">Loading…</div>;
  if (!list.data.length) return <p className="faint" style={{ margin: 0 }}>No conversations in the last 30 days.</p>;
  return (
    <ul className="help-history">
      {list.data.map((p) => (
        <li key={p.id}>
          <button type="button" className="help-history-item" onClick={() => open(p.id)}>
            <span className="item-title">{p.title}</span>
            <span className="faint small"><Time iso={p.last_at} />{p.open ? ' · open' : ''}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function EmailMe({ thread }: { thread: string }) {
  const [state, setState] = useState<{ busy: boolean; to: string | null; error: unknown }>({ busy: false, to: null, error: null });
  const send = async () => {
    setState({ busy: true, to: null, error: null });
    try { setState({ busy: false, to: (await api<{ to: string }>(`/v1/help/conversations/${thread}/email`, { body: {} })).to, error: null }); }
    catch (error) { setState({ busy: false, to: null, error }); }
  };
  return (
    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
      <Button type="button" variant="ghost" size="sm" onClick={() => void send()} disabled={state.busy}><Icon name="mail" />Email me this conversation</Button>
      <span role="status" className="faint small">{state.to && `On its way to ${state.to}.`}</span>
      <ErrorBanner error={state.error} />
    </div>
  );
}
